import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  checkUsernameAvailabilityRequest,
  clearUsernameCheck,
} from '_admin/features/users/usersSlice';
import {
  selectUsernameChecking,
  selectUsernameAvailability,
  selectUsernameMessage,
  selectUsernameError,
} from '_admin/features/users/usersSelectors';

export const useUsernameCheck = () => {
  const dispatch = useDispatch();
  const [usernameTouched, setUsernameTouched] = useState(false);
  const debounceTimeoutRef = useRef(null);

  const checking = useSelector(selectUsernameChecking);
  const available = useSelector(selectUsernameAvailability);
  const message = useSelector(selectUsernameMessage);
  const error = useSelector(selectUsernameError);

  const clearDebounce = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  const checkUsername = useCallback((username) => {
    clearDebounce();
    const alphanumericRegex = /^[a-zA-Z0-9]+$/;
    const isValidFormat =
      !!username && username.length >= 6 && alphanumericRegex.test(username);

    if (!isValidFormat) {
      dispatch(clearUsernameCheck());
      return;
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setUsernameTouched(true);
      dispatch(checkUsernameAvailabilityRequest({ username }));
    }, 500);
  }, [dispatch, clearDebounce]);

  const reset = useCallback(() => {
    clearDebounce();
    setUsernameTouched(false);
    dispatch(clearUsernameCheck());
  }, [dispatch, clearDebounce]);

  useEffect(() => {
    return () => clearDebounce();
  }, [clearDebounce]);

  const result = available !== null ? { available, message } : null;

  return {
    checking,
    result,
    error,
    checkUsername,
    reset,
    usernameTouched,
  };
};